/** A landing page's campaign parameters as captured, plus the `capturedAt` bookkeeping field. */
export type CampaignTouch = Record<string, string>;

/**
 * When the stored touch was captured. Reserved: it is bookkeeping, not attribution, so it never
 * rides a link or metadata. Stored as a string because the storage schema is a string record.
 */
export const ATTRIBUTION_CAPTURED_AT_KEY = 'capturedAt';

/**
 * Meta's click attribution window is 7 days, so a click id older than that is misattributed by
 * definition — without a TTL one ad click keeps crediting a browser's purchases to the campaign
 * forever. TikTok's window is no longer, so a single bound covers both.
 */
export const ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Undated attribution counts as stale: records written before the TTL existed cannot be dated, and
 * claiming paid credit for a touch of unknown age is the error this TTL exists to prevent. A
 * timestamp in the future (clock skew, or a tampered record) is stale for the same reason —
 * otherwise it would never expire.
 */
export const isAttributionFresh = (attribution: CampaignTouch, now: number): boolean => {
  const capturedAt = Number(attribution[ATTRIBUTION_CAPTURED_AT_KEY]);
  if (!Number.isFinite(capturedAt)) {
    return false;
  }
  const age = now - capturedAt;
  return age >= 0 && age <= ATTRIBUTION_TTL_MS;
};

/** The ad URL parameters a campaign touch is made of: UTMs, campaign structure and click ids. */
export const CAMPAIGN_TOUCH_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'campaign',
  'campaign_id',
  'adgroup',
  'adgroup_id',
  'creative',
  'creative_id',
  'fbclid',
  'ttclid',
  'gclid',
  'gbraid',
  'wbraid',
] as const;

// Values are visitor-controlled, so a crafted URL cannot grow the stored record without bound.
const MAX_TOUCH_VALUE_LENGTH = 500;

export const captureCampaignTouch = (search: string): CampaignTouch => {
  const searchParams = new URLSearchParams(search);
  const touch: CampaignTouch = {};

  CAMPAIGN_TOUCH_QUERY_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) {
      touch[key] = value.slice(0, MAX_TOUCH_VALUE_LENGTH);
    }
  });

  return touch;
};

const isSameTouch = (stored: CampaignTouch, current: CampaignTouch): boolean =>
  CAMPAIGN_TOUCH_QUERY_KEYS.every((key) => stored[key] === current[key]);

/**
 * Last-touch: a URL carrying campaign parameters replaces the stored touch and starts its TTL,
 * unless it is the same touch re-read (a reload or a locale switch on the landing URL), which keeps
 * its original stamp so browsing cannot extend one click's window. A URL without campaign
 * parameters leaves the stored touch as it is.
 */
export const mergeCampaignTouch = (stored: CampaignTouch, current: CampaignTouch, now: number): CampaignTouch => {
  if (Object.keys(current).length === 0) {
    return stored;
  }
  const storedCapturedAt = stored[ATTRIBUTION_CAPTURED_AT_KEY];
  const isRepeat = storedCapturedAt !== undefined && isSameTouch(stored, current);

  return { ...current, [ATTRIBUTION_CAPTURED_AT_KEY]: isRepeat ? storedCapturedAt : String(now) };
};
